import { createPerfilApiClient } from './perfilApiClient';

function getAuthParams() {
    const token = localStorage.getItem('token');
    const tid = localStorage.getItem('hash_teatro');
    return {
        api_key: token,
        ...(tid ? { tid } : {}),
    };
}

export default {
    /**
     * Obtiene el grupo familiar de un abonado específico.
     * GET /f/perfilV2/abonados/{abonadoId}/familia
     *
     * @param {number|string} abonadoId
     * @returns {Promise<Object|null>}
     */
    async getFamiliaByAbonado(abonadoId) {
        try {
            const response = await createPerfilApiClient().get(
                `/f/perfilV2/abonados/${abonadoId}/familia`,
                { params: getAuthParams() }
            );
            return response.data?.data ?? null;
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[familia] Error al obtener familia del abonado:', error);
            }
            throw error;
        }
    },

    /**
     * Obtiene los miembros de la familia de un abonado específico con paginación.
     * GET /f/perfilV2/abonados/{abonadoId}/familia/miembros
     *
     * @param {number|string} abonadoId
     * @param {number} page
     * @param {number} perPage
     * @returns {Promise<{items: Array, meta: Object}>}
     */
    async getMiembrosFamilia(abonadoId, page = 1, perPage = 5) {
        try {
            const response = await createPerfilApiClient().get(
                `/f/perfilV2/abonados/${abonadoId}/familia/miembros`,
                { params: { ...getAuthParams(), page, per_page: perPage } }
            );
            const data = response.data?.data ?? [];
            return {
                items: Array.isArray(data) ? data : [],
                meta: response.data?.meta ?? {},
            };
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[familia] Error al obtener miembros de la familia:', error);
            }
            throw error;
        }
    },

    /**
     * Valida un abonado antes de añadirlo a una familia.
     * POST /f/perfilV2/abonados/{abonadoId}/familia/miembros/validar
     *
     * @param {number|string} abonadoId - Actor que realiza la validación
     * @param {string} numeroAbonado
     * @param {string} dni
     * @returns {Promise<Object|null>}
     */
    async validarMiembro(abonadoId, numeroAbonado, dni) {
        try {
            const response = await createPerfilApiClient().post(
                `/f/perfilV2/abonados/${abonadoId}/familia/miembros/validar`,
                { numero_abonado: numeroAbonado, dni },
                { params: getAuthParams() }
            );
            return response.data?.data ?? response.data ?? null;
        } catch (error) {
            if (error.response?.status === 404) return null;
            if (process.env.NODE_ENV !== 'production') {
                console.error('[familia] Error al validar miembro:', error);
            }
            throw error;
        }
    },

    /**
     * Añade un abonado a una familia.
     * POST /f/perfilV2/abonados/{abonadoId}/familia/miembros
     *
     * @param {number|string} abonadoId - Actor que realiza la acción
     * @param {string} numeroAbonado
     * @param {string} dni
     * @returns {Promise<Object>}
     */
    async agregarMiembro(abonadoId, numeroAbonado, dni) {
        try {
            const response = await createPerfilApiClient().post(
                `/f/perfilV2/abonados/${abonadoId}/familia/miembros`,
                { numero_abonado: numeroAbonado, dni },
                { params: getAuthParams() }
            );
            return response.data?.data ?? response.data ?? {};
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[familia] Error al agregar miembro:', error);
            }
            throw error;
        }
    },

    /**
     * Crea un grupo familiar explícitamente.
     * POST /f/perfilV2/abonados/{abonadoId}/familia
     *
     * El actor (abonadoId) se convierte en titular.
     * Rechaza con 422 si el actor ya pertenece a una familia.
     *
     * @param {number|string} abonadoId - Actor que será el titular
     * @param {string} nombre - Nombre del grupo familiar
     * @returns {Promise<Object>}
     */
    async crearFamilia(abonadoId, nombre) {
        try {
            const response = await createPerfilApiClient().post(
                `/f/perfilV2/abonados/${abonadoId}/familia`,
                { nombre },
                { params: getAuthParams() }
            );
            return response.data?.data ?? response.data ?? {};
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[familia] Error al crear familia:', error);
            }
            throw error;
        }
    },

    /**
     * Actualiza el nombre del grupo familiar.
     * PATCH /f/perfilV2/abonados/{abonadoId}/familia
     *
     * @param {number|string} abonadoId - Actor (solo titular)
     * @param {string} nombre - Nuevo nombre del grupo familiar
     * @returns {Promise<Object>}
     */
    async actualizarNombre(abonadoId, nombre) {
        try {
            const response = await createPerfilApiClient().patch(
                `/f/perfilV2/abonados/${abonadoId}/familia`,
                { nombre },
                { params: getAuthParams() }
            );
            return response.data?.data ?? response.data ?? {};
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[familia] Error al actualizar nombre:', error);
            }
            throw error;
        }
    },

    /**
     * Desvincula un miembro del grupo familiar.
     * DELETE /f/perfilV2/abonados/{abonadoId}/familia/miembros/{miembroId}
     *
     * Reglas:
     * - Titular puede quitar a cualquier miembro (incluido él mismo)
     * - Miembro regular solo puede desvincularse a sí mismo
     *
     * @param {number|string} abonadoId - Actor que realiza la acción
     * @param {number|string} miembroId - Abonado a desvincular
     * @returns {Promise<{deleted: boolean, message: string, familia?: Object}>}
     */
    async removerMiembro(abonadoId, miembroId) {
        try {
            const response = await createPerfilApiClient().delete(
                `/f/perfilV2/abonados/${abonadoId}/familia/miembros/${miembroId}`,
                { params: getAuthParams() }
            );
            return response.data ?? {};
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[familia] Error al remover miembro:', error);
            }
            throw error;
        }
    },
};
